// Module 3 — Well Profile Editor.
//
// Six sections plus a Casing Program subtable:
//   1. Identity
//   2. Classification
//   3. Depths & Datums
//   4. Surface Location
//   5. Status & Timeline
//   6. BOP Reference (picker → Module 2 BOP library)
//   7. Casing Program (multi-row picker → Module 2 Casing library)
//   8. Notes
//
// References (not copies) into the Reference Libraries are stored as ID
// strings (`bopRefId`, `casingRefId`). Display fields on each casing row are
// denormalised copies for instant readability — never edited directly; they
// are refreshed every time the user re-picks the library record.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import {
  ArrowLeft, Save, AlertTriangle, Plus, Trash2, Library, Trash, CheckCircle2, AlertCircle,
} from "lucide-react";
import { Field, TextInput, TextArea } from "@/components/ReportPrimitives";
import FloatingActionToolbar from "@/components/FloatingActionToolbar";
import UnsavedChangesDialog from "@/components/UnsavedChangesDialog";
import useUnsavedChanges from "@/hooks/useUnsavedChanges";
import {
  emptyWellProfile, emptyCasingRow, validateWellProfile, validateWellProfileSection, migrateLegacyWellProfile,
  WELL_TYPES, WELL_PURPOSES, WELL_OBJECTIVES, ENVIRONMENTS, BOP_LOCATIONS,
  WELL_STATUSES, COORD_REF_SYSTEMS, HOLE_SECTIONS,
} from "@/lib/well/schema";
import {
  createWellProfile, updateWellProfile, getWellProfile, deleteWellProfile,
} from "@/lib/well/storage";
import { listRigSheets, getRigSheet, updateRigSheet } from "@/lib/rigsheet/storage";
import { getEngineeringSession, updateEngineeringSession } from "@/lib/rce/session";
import { resolve } from "@/lib/rce/resolver";
import { sessionToResolverCtx } from "@/lib/rce/useReportRceWiring";
import { listBhasForWell } from "@/lib/bha/storage";
import { listDrillStringsForWell } from "@/lib/drillstring/storage";
import { listKillSheetsForWell } from "@/lib/killsheet/storage";
import { listMudProgramsForWell } from "@/lib/mud/program";
// Completion criteria live in lib/well/completion.js so the Dashboard's
// "Well Profiles" badge and this editor's per-section badges can never
// disagree. Aliased to the previous local names to keep call sites intact.
import {
  sectionRequired as _sectionRequired,
  sectionHasData as _sectionHasData,
  isKillSheetPrerequisite,
  missingKillSheetPrerequisites,
  WELL_SECTION_FLOW,
} from "@/lib/well/completion";
import { listByCategory, findById } from "@/lib/reflibs/lookup";
import { fillBlanksFromLibrary } from "@/lib/reflibs/libraryFill";
import { libraryReturnState, getRestoredDraft, getRestoredAnchor, scrollToRestoredAnchor } from "@/lib/reflibs/returnContext";
import { useStackMode } from "@/lib/stackModeContext";
import { stackAccentClasses } from "@/lib/stackAccent";
import { buildWellState } from "@/lib/openg/wellState";
import { computeBhaGeometry } from "@/lib/bha/schema";
import { getBha } from "@/lib/bha/storage";
import RceEditorStrip from "@/components/rce/RceEditorStrip";
import { toast } from "sonner";

// P0 fix (Phase 3) — sentinel for handleSave's advanceTo param, see its use
// below. Not a route; opens the canonical Session Switcher instead.
const OPEN_SESSION_SWITCHER = "__open_session_switcher__";

// --- small UI primitives (mirroring conventions in reflibs editor) ---------
function NativeSelect({ value, onChange, options, testId, disabled }) {
  return (
    <select value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            data-testid={testId}
            className="input-rs">
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
function NumIn(props) { return <TextInput type="number" {...props} />; }

// Which casing dimensions are manual overrides, and what the linked library
// record says instead (source precedence: manual → library → missing).
function _casingOverrideNote(row) {
  const entered = (v) => v !== "" && v != null;
  const ref = row?.casingRefId ? findById(row.casingRefId) : null;
  const parts = [];
  if (entered(row?.manualInnerDiameterIn)) {
    parts.push(`ID ${row.manualInnerDiameterIn} in${ref && entered(ref.innerDiameter) ? ` (library ${ref.innerDiameter} in)` : ""}`);
  }
  if (entered(row?.manualOuterDiameterIn)) {
    parts.push(`OD ${row.manualOuterDiameterIn} in${ref && entered(ref.outerDiameter) ? ` (library ${ref.outerDiameter} in)` : ""}`);
  }
  return parts.join(" · ");
}
function DateIn(props) { return <TextInput type="date"   {...props} />; }

// --- helpers ----------------------------------------------------------------
function _bopLabel(rec) {
  if (!rec) return "(missing — record may have been removed)";
  const parts = [rec.stackName, rec.nominalSize, rec.workingPressure ? `${rec.workingPressure} psi` : null];
  return parts.filter(Boolean).join(" · ");
}
function _casingLabel(rec) {
  if (!rec) return "(missing — record may have been removed)";
  const parts = [rec.nominalSize, rec.grade, rec.connection];
  return parts.filter(Boolean).join(" · ");
}

// Correction 2 — Well Profile prefill. Exact existing-field matches only,
// resolved through the existing RCE resolver (no parallel sync system).
// Each entry lists candidate rcePaths in priority order — the first one that
// resolves wins. Five-Defect Correction Pass, Defect 2 root cause: Operator
// and Country previously only checked the Project Profile, which almost no
// session has active (no simple "create a Project" flow exists yet) — the
// active Rig Data Sheet already stores its own operator/country and now has
// registered derivers for them (see lib/rce/derivers.js), so they resolve
// even with no Project set, exactly like Rig name/Rig contractor already do.
// Client has NO existing authoritative source anywhere (no Rig field, no
// other canonical store) — stays manual. Field/Block now resolve from the
// Rig Data Sheet first: it stores them as two separate, authoritative
// fields (schema.js), so this is an exact 1:1 match, not a split of the
// legacy combined `fieldOrBlock` free-text string — which nothing derives
// from, precisely because splitting it would be a guess.
const WELL_PREFILL_MAP = [
  ["operator",      ["rig.operator", "project.operator"], "Operator"],
  ["country",       ["rig.country",  "project.country"],  "Country"],
  ["field",         ["rig.field",    "project.field"],    "Field"],
  ["block",         ["rig.block",    "project.block"],    "Block"],
  ["rigName",       ["rig.name"],                          "Rig name"],
  ["rigContractor", ["rig.contractor"],                    "Rig contractor"],
  // "client" intentionally omitted — no reliable existing source.
];

// =============================================================================
// Editor
// =============================================================================
export default function WellProfileEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isNew = !id;

  const [rec, setRec]       = useState(() => emptyWellProfile());
  const [errors, setErrors] = useState([]);
  // Per-section Save errors (follow-up, ticket 237121) — keyed by sectionKey.
  // Kept separate from `errors` (the whole-record / overall-readiness banner)
  // so a section-scoped Save failure surfaces locally, next to that section,
  // instead of pulling the page to the top-of-form banner.
  const [sectionErrors, setSectionErrors] = useState({});
  const [notFound, setNotFound] = useState(false);
  const [autoLinkedRig, setAutoLinkedRig] = useState(null); // { rigId, rigName } after auto-link
  const [rigChoicePending, setRigChoicePending] = useState(false); // when 2+ rigs and none auto-linked
  const [prefilledFields, setPrefilledFields] = useState([]); // Correction 2 — labels shown to the operator

  useEffect(() => {
    if (isNew) {
      setRec(emptyWellProfile());
      setNotFound(false);
      return;
    }
    const row = getWellProfile(id);
    if (row) {
      // migrateLegacyWellProfile fills in rigName/rigContractor from legacy
      // `rig`, ensures every child container exists, and zero-fills new optional
      // fields without touching anything the user actually set.
      setRec(migrateLegacyWellProfile(row));
      setNotFound(false);
    } else {
      setNotFound(true);
    }
  }, [id, isNew]);

  // Auto-scroll to the section indicated by URL hash after returning from a
  // child module (BHA / Drill String / Mud Program / Kill Sheet). Retries a
  // couple of times because the child sections mount only when `!isNew`.
  useEffect(() => {
    if (isNew || !window.location.hash) return;
    const anchor = window.location.hash.slice(1);
    const scroll = () => {
      const el = document.getElementById(anchor);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    const t1 = window.setTimeout(scroll, 250);
    const t2 = window.setTimeout(scroll, 900);
    return () => { window.clearTimeout(t1); window.clearTimeout(t2); };
  }, [isNew, rec.id]);

  // Correction 2 — Well Profile prefill. Brand-new wells only; a saved well
  // never gets touched by this effect. Reuses the existing resolver against
  // the active Engineering Session (Project / Rig picked in SessionSwitcher).
  useEffect(() => {
    if (!isNew) return;
    const session = getEngineeringSession();
    if (!session) return;
    const ctx = sessionToResolverCtx(session);
    const patch = {};
    const filled = [];
    for (const [wellField, rcePaths, label] of WELL_PREFILL_MAP) {
      for (const rcePath of rcePaths) {
        const r = resolve(rcePath, ctx);
        if (!r || r.confidence === "unknown" || r.confidence === "conflict") continue;
        if (r.value === "" || r.value == null) continue;
        patch[wellField] = String(r.value);
        filled.push(label);
        break;
      }
    }
    if (Object.keys(patch).length > 0) {
      setRec((cur) => ({ ...cur, ...patch }));
      setPrefilledFields(filled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew]);

  // Reference Library Return-Workflow — restore in-progress edits that
  // survived a round trip to a Reference Library (Manage BOP/Casing
  // Library → Add/Edit Record → ← BACK). Consumed once, then cleared from
  // history state so a later refresh never re-applies a stale draft. Runs
  // after the load/prefill effects above so it always wins on mount.
  useEffect(() => {
    const draft = getRestoredDraft(location);
    if (!draft) return;
    const matches = isNew ? !draft.id : draft.id === id;
    if (matches) setRec(draft);
    scrollToRestoredAnchor(getRestoredAnchor(location));
    navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Well Identity first-save correction (P0) — local "Saved" confirmation
  // shown at the Identity section itself (not just the generic top-level
  // toast) after the brand-new-well first save lands on /wells/:id with
  // `restoreAnchorId: "well-section-identity"`. Read once on mount, not
  // cleared from history state (unlike the draft-restore effect above) —
  // ScrollToTop's own restore effect is still consuming the same location
  // state on this same mount, and clearing it here first would race that
  // effect's dependency array and cancel the anchor scroll.
  const [identitySavedFlash, setIdentitySavedFlash] = useState(false);
  useEffect(() => {
    if (location.state?.restoreAnchorId !== "well-section-identity") return;
    setIdentitySavedFlash(true);
    const t = setTimeout(() => setIdentitySavedFlash(false), 3000);
    return () => clearTimeout(t);
    // Reacts to `location.state` itself (not `[]`) — WellProfileEditor does
    // NOT remount between /wells/new and /wells/:id (same component at the
    // same Outlet position, only `useParams().id` changes), so a mount-only
    // effect here would have already run — and found nothing — before the
    // first-save navigate() call that sets this state ever happens.
  }, [location.state]);

  const set = (patch) => setRec((cur) => ({ ...cur, ...patch }));

  // Subsea Foundation Correction Pass — the global operating mode is the
  // single existing authoritative source (no new flag). SUBSEA always
  // resolves Environment=Offshore + BOP Location=Subsea; SURFACE always
  // resolves BOP Location=Surface (Environment stays free — Surface BOP
  // legitimately serves both Onshore and Offshore-Surface wells).
  const { stackMode } = useStackMode();
  const accent = stackAccentClasses(stackMode);
  const envLocked = stackMode === "subsea";
  const effectiveEnvironment  = envLocked ? "offshore" : rec.environment;
  const effectiveBopLocation = stackMode === "subsea" ? "subsea" : "surface";

  // RC-1 · Unsaved Changes Protection (presentation only). Enabled once the
  // well is saved (isNew=false); for a brand-new well the operator is
  // expected to press Save Identity first anyway.
  const unsaved = useUnsavedChanges({ data: rec, enabled: !isNew && !notFound, onSave: () => handleSave() });

  // Reference Library options ------------------------------------------------
  const bopOptions = useMemo(() => {
    const all = listByCategory("bop").filter((r) => r.bopType === effectiveBopLocation);
    const opts = [
      { value: "", label: "— No BOP selected —" },
      ...all.map((r) => ({ value: r.id, label: _bopLabel(r) })),
    ];
    // Surface a context mismatch instead of silently hiding/replacing an
    // existing saved reference (Subsea Foundation Correction Pass).
    if (rec.bopRefId && !all.some((r) => r.id === rec.bopRefId)) {
      const existing = findById(rec.bopRefId);
      opts.push({ value: rec.bopRefId, label: `${existing ? _bopLabel(existing) : rec.bopRefId} (context mismatch)` });
    }
    return opts;
  }, [effectiveBopLocation, rec.bopRefId]);
  const bopContextMismatch = !!rec.bopRefId
    && !listByCategory("bop").some((r) => r.id === rec.bopRefId && r.bopType === effectiveBopLocation);
  const casingOptions = useMemo(() => {
    const all = listByCategory("casing");
    return [
      { value: "", label: "— Pick from casing library —" },
      ...all.map((r) => ({ value: r.id, label: _casingLabel(r) })),
    ];
  }, []);

  // Casing program row mutation helpers --------------------------------------
  const _setCasing = (rows) => set({ children: { ...rec.children, casingProgram: rows } });

  const addCasingRow = () => {
    const next = [...(rec.children?.casingProgram || [])];
    const row = { ...emptyCasingRow(), id: `csg_${next.length + 1}` };
    next.push(row);
    _setCasing(next);
  };
  const removeCasingRow = (idx) => {
    const next = [...(rec.children?.casingProgram || [])];
    next.splice(idx, 1);
    _setCasing(next);
  };
  const patchCasingRow = (idx, patch) => {
    const next = [...(rec.children?.casingProgram || [])];
    next[idx] = { ...next[idx], ...patch };
    _setCasing(next);
  };
  const pickCasing = (idx, refId) => {
    if (!refId) {
      patchCasingRow(idx, {
        casingRefId: "", nominalSize: "", weightPpf: "", grade: "", connection: "",
      });
      return;
    }
    const ref = findById(refId);
    if (!ref) return;
    // Manual-first precedence (see emptyCasingRow in lib/well/schema.js):
    // picking a library record fills the operator's dimension fields ONLY
    // where they are still blank, and never overwrites a value already
    // entered. fillBlanksFromLibrary is the canonical helper for exactly
    // this rule, so casing now behaves like the BHA and Drill String
    // editors instead of leaving OD/ID blank for the operator to retype.
    //
    // Library records store these as numbers (seed.js projects
    // `Number(raw.od_in)`), while the NumIn inputs bind to strings — hence
    // the conversion. A non-finite value maps to "", which
    // fillBlanksFromLibrary treats as "nothing to fill".
    const row = (rec.children?.casingProgram || [])[idx] || {};
    const _libNum = (v) => (v != null && v !== "" && Number.isFinite(Number(v)) ? String(v) : "");
    const dims = fillBlanksFromLibrary(
      {
        manualInnerDiameterIn: row.manualInnerDiameterIn,
        manualOuterDiameterIn: row.manualOuterDiameterIn,
      },
      {
        manualInnerDiameterIn: _libNum(ref.innerDiameter),
        manualOuterDiameterIn: _libNum(ref.outerDiameter),
      },
    );
    patchCasingRow(idx, {
      casingRefId: ref.id,
      nominalSize: ref.nominalSize || "",
      weightPpf:   ref.nominalWeightPpf || ref.weightPpf || "",
      grade:       ref.grade || "",
      connection:  ref.connection || "",
      ...dims,
    });
  };

  // Save / Delete ------------------------------------------------------------
  // Auto-link the newly-saved Well to a Rig Data Sheet when RigSentinel can
  // unambiguously identify the target. Rules:
  //   1) If the Engineering Session already names an active rig, use it.
  //   2) Else if exactly one Rig Data Sheet exists, use that one.
  //   3) Else (2+ rigs and no active) → surface a picker chip; do not guess.
  // In all cases, never overwrite a rig that is already linked to a different
  // well (respect prior operator intent).
  const _autoLinkToRigSheet = (wellId) => {
    try {
      const sess = getEngineeringSession();
      const rigs = listRigSheets();
      let targetId = "";
      if (sess?.rigId && rigs.some((r) => r.id === sess.rigId)) {
        targetId = sess.rigId;
      } else if (rigs.length === 1) {
        targetId = rigs[0].id;
      } else if (rigs.length >= 2) {
        setRigChoicePending(true);
        return;
      }
      if (!targetId) return; // zero rig sheets — nothing to link
      const target = getRigSheet(targetId);
      if (!target) return;
      // `getRigSheet` returns a versioned row: { id, version, payload:{…} }.
      const currentPayload = target.payload || {};
      const currentLinked = currentPayload.linkedWellId || "";
      if (currentLinked && currentLinked !== wellId) return; // don't overwrite
      if (currentLinked === wellId) return; // already linked — no-op
      // updateRigSheet replaces the entire payload, so merge instead of patching.
      updateRigSheet(targetId, { ...currentPayload, linkedWellId: wellId });
      setAutoLinkedRig({ rigId: targetId, rigName: currentPayload.rigName || target.label || "Rig Data Sheet" });
    } catch {
      // Auto-link is a convenience — never block Save on a link failure.
    }
  };

  const handleSave = (nextSectionKey, advanceTo, savedSectionKey) => {
    // Subsea Foundation Correction Pass — coerce Environment/BOP Location to
    // match the current global operating mode only at the point of an
    // explicit Save (never silently in the background beforehand). `rec`
    // itself is only updated to the coerced values after a successful save.
    const coerced = stackMode === "subsea"
      ? { ...rec, environment: "offshore", bopLocation: "subsea" }
      : { ...rec, bopLocation: "surface" };
    // Save vs. Complete correction (follow-up) — record that this specific
    // section was explicitly saved, so Classification/Status (whose fields
    // ship with non-empty defaults) can require a real Save before showing
    // Completed, not just "still equal to the schema default".
    if (savedSectionKey) {
      coerced.sectionsTouched = { ...(rec.sectionsTouched || {}), [savedSectionKey]: true };
    }
    // Per-section Save validates only that section's own fields (follow-up,
    // ticket 237121) — a requirement belonging to a later section (there is
    // exactly one: offshore ⇒ positive water depth) must never refuse an
    // earlier section's work-in-progress save. The bottom "Save Well
    // Profile"/"Save & continue" buttons and the leave-page autosave call
    // this with no savedSectionKey, so they still run the full whole-record
    // check — that remains the overall-readiness gate, unchanged.
    const v = savedSectionKey
      ? validateWellProfileSection(coerced, savedSectionKey)
      : validateWellProfile(coerced);
    if (!v.ok) {
      if (savedSectionKey) {
        setSectionErrors((prev) => ({ ...prev, [savedSectionKey]: v.errors }));
        const el = document.getElementById(`well-section-${savedSectionKey}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        setErrors(v.errors);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      // Explicit `false` — useUnsavedChanges tests `result === false` to keep
      // the Save-and-Leave dialog open on a failed save. A bare `return`
      // yields undefined, which it reads as success and navigates away,
      // silently dropping the operator's unsaved work.
      return false;
    }
    if (savedSectionKey) {
      setSectionErrors((prev) => (prev[savedSectionKey]?.length ? { ...prev, [savedSectionKey]: [] } : prev));
    }
    try {
      const wasNew = isNew;
      const saved = isNew
        ? createWellProfile(coerced, { sectionScoped: savedSectionKey })
        : updateWellProfile(id, coerced, { sectionScoped: savedSectionKey });
      setRec(saved || coerced);
      setErrors([]);
      if (wasNew) _autoLinkToRigSheet(saved.id);
      // Root-cause repair (Master Correction Pass, Defect 2/1) — auto-activate
      // this well in the Engineering Session on first save, mirroring the
      // existing auto-link-to-rig behaviour above. Without this, later pages
      // that resolve canonical data from the session (Operational State,
      // Well Status here) have no wellId to resolve against unless the
      // operator manually opens the Session Switcher. Never overwrites an
      // already-active well binding.
      if (wasNew) {
        const s = getEngineeringSession();
        if (!s || !s.wellId) updateEngineeringSession({ wellId: saved.id });
      }
      unsaved.markSaved(saved || rec);
      toast.success(wasNew ? "Well Profile created" : "Well Profile saved");
      if (advanceTo) {
        // P0 fix (Phase 3) — this used to be a real route ("/profiles/opstate/new"),
        // navigating a just-saved canonical Well Profile straight into the
        // legacy rsc.profiles.opstate.v1 editor: a duplicate data-entry
        // surface for the same MD/TVD/hole-size/mud data just entered above.
        // The sentinel below opens the canonical Session Switcher instead —
        // still only reached after a genuinely successful, validated save.
        if (advanceTo === OPEN_SESSION_SWITCHER) {
          window.dispatchEvent(new CustomEvent("rce:open-session-switcher"));
          return true;
        }
        navigate(advanceTo);
        return true;
      }
      // Defect 8 root cause — `navigate()` to a pathname that hasn't actually
      // changed still fires the global ScrollToTop effect, which resets the
      // window to top a frame after any scroll-preserve logic here runs.
      // Only navigate when the URL genuinely needs to change (brand-new
      // well: /wells/new → /wells/:id). Every other save already sits on
      // the correct URL — no navigation, nothing for ScrollToTop to react
      // to, so the operator's position is naturally preserved.
      if (wasNew) {
        // Well Identity first-save correction (P0) — element-anchored
        // restoration, not pixel-based. ContinueSetupPanel only mounts
        // once `isNew` is false, shifting the Identity section down by a
        // fixed amount (~272px) that a captured pixel offset can never
        // predict — anchoring to the Identity section itself is correct
        // regardless of how much the layout shifts. `identitySavedFlash`
        // triggers the local "Saved" confirmation rendered at that same
        // section (see the effect + banner near the Identity section).
        navigate(`/wells/${saved.id}`, {
          state: { restoreAnchorId: "well-section-identity", identitySavedFlash: true },
        });
        return true;
      }
      // Save-and-advance: a successful per-section Save scrolls to the next
      // section so the data-entry order is discoverable (save Mud Program →
      // land on Casing).
      //
      // This deliberately reverses the earlier "D2 fix (Five-Defect
      // Correction Pass)", which removed auto-advance because it read as
      // "Save jumps me away from where I was". Re-requested as explicit
      // flow guidance. Scoped tightly so that complaint cannot return
      // elsewhere: it fires ONLY for a per-section Save (`savedSectionKey`)
      // that actually has a following section — never for the bottom
      // "Save Well Profile" button, the floating toolbar, the leave-page
      // autosave, a failed save, or the last section.
      if (savedSectionKey && nextSectionKey) {
        const nextEl = document.getElementById(`well-section-${nextSectionKey}`);
        // rAF so the scroll runs after this save's re-render has settled;
        // the section heights change as completion badges appear.
        if (nextEl) {
          window.requestAnimationFrame(() => {
            nextEl.scrollIntoView({ behavior: "smooth", block: "start" });
          });
        }
      }
      return true;
    } catch (e) {
      const msg = [].concat(e.validationErrors || [e.message || "Save failed"]);
      if (savedSectionKey) {
        setSectionErrors((prev) => ({ ...prev, [savedSectionKey]: msg }));
      } else {
        setErrors(msg);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return false;
    }
  };
  const handleDelete = () => {
    if (!id) return;
    if (!window.confirm(`Delete well "${rec.wellName}"?\n\nThis removes the well only. Reference libraries are untouched.`))
      return;
    deleteWellProfile(id);
    navigate("/wells");
  };

  if (notFound) {
    return (
      <div className="card-rs p-6 text-sm text-[var(--rs-text-dim,#9aa6b2)]">
        Well not found. <Link to="/wells" className="text-amber-400">Back to Wells</Link>.
      </div>
    );
  }

  const offshore = effectiveEnvironment === "offshore";

  return (
    <div data-testid="well-editor" className="flex flex-col gap-4 pb-8">
      <header className="flex items-center justify-between gap-3">
        <button type="button" data-testid="well-editor-back"
                onClick={() => unsaved.guardedNavigate("/wells")}
                className="inline-flex items-center gap-1.5 text-[12px] text-[var(--rs-text-2)] hover:text-[var(--rs-amber)]">
          <ArrowLeft className="w-3.5 h-3.5" /> Wells
        </button>
        <div className="flex items-center gap-2">
          {!isNew && (
            <button type="button"
                    onClick={handleDelete}
                    data-testid="well-editor-delete"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] text-red-300 border border-red-500/30 hover:bg-red-500/10">
              <Trash className="w-3.5 h-3.5" /> Delete
            </button>
          )}
          <button type="button"
                  onClick={handleSave}
                  data-testid="well-editor-save"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium"
                  style={{ background: "var(--rs-amber, #f5b400)", color: "#0A0B0E" }}>
            <Save className="w-3.5 h-3.5" /> Save
          </button>
        </div>
      </header>

      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--rs-text-dim,#9aa6b2)]">
          Module 3 · Well Profile
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold mt-0.5 text-[var(--rs-text,#e8edf2)]">
          {isNew ? "New Well" : (rec.wellName || "(unnamed well)")}
        </h1>
      </div>

      <RceEditorStrip
        ownerKey="wellProfile"
        ownerLabel="Well Profile · Engineering Context"
        consumes={["project.operator", "project.client", "project.country", "project.field"]}
      />

      {/* Well Status Summary — the operational dashboard for this well.
          Many cells will be blank in pass 1 — future Module 3.x / Module 4
          passes will populate the placeholders automatically. */}
      <WellStatusSummary rec={rec} />

      {/* Continue Setup strip + auto-link toast + rig-choice picker.
          Rendered only for saved wells so it appears immediately after a
          successful Save (post-save scroll brings it into the viewport). */}
      {!isNew && (
        <ContinueSetupPanel
          wellId={id}
          autoLinkedRig={autoLinkedRig}
          onDismissAutoLink={() => setAutoLinkedRig(null)}
          rigChoicePending={rigChoicePending}
          onPickRig={(rigId) => {
            try {
              const target = getRigSheet(rigId);
              if (!target) return;
              const currentPayload = target.payload || {};
              const currentLinked  = currentPayload.linkedWellId || "";
              if (!currentLinked || currentLinked === id) {
                updateRigSheet(rigId, { ...currentPayload, linkedWellId: id });
                setAutoLinkedRig({ rigId, rigName: currentPayload.rigName || target.label || "Rig Data Sheet" });
              }
            } finally {
              setRigChoicePending(false);
            }
          }}
          onDismissRigChoice={() => setRigChoicePending(false)}
        />
      )}

      {/* Well Sections Progress — Commercial Workflow QA (Well Profile Polish).
          Presentation-only overview of the eight inline sections completed on
          this page. Purely derived from `rec`; no schema/storage change. */}
      <WellSectionsProgress rec={rec} isNew={isNew} />

      {errors.length > 0 && (
        <div data-testid="well-editor-errors"
             className="card-rs p-3 border-red-500/30 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
          <ul className="text-xs text-red-300 list-disc list-inside space-y-0.5">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {/* 1. Identity --------------------------------------------------------*/}
      <div id="well-section-identity">
      <section className="card-rs p-4">
        <SectionHeader title="Identity" sectionKey="identity" rec={rec} isNew={isNew} />
        {identitySavedFlash && (
          <div data-testid="well-identity-saved-flash"
               className="mb-3 flex items-center gap-1.5 text-[11.5px] font-medium text-emerald-300 px-2.5 py-1.5 rounded-md"
               style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.35)" }}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Saved — Well Identity
          </div>
        )}
        {isNew && prefilledFields.length > 0 && (
          <div data-testid="well-prefill-banner"
               className="text-[10.5px] text-[var(--rs-text-2)] mb-3 leading-snug px-2.5 py-1.5 rounded-md"
               style={{ background: "rgba(120,200,255,0.06)", border: "1px solid rgba(120,200,255,0.25)" }}>
            <span className="font-mono uppercase tracking-[0.16em] text-[9.5px]" style={{ color: "#78c8ff" }}>
              Pre-filled ·
            </span>{" "}
            {prefilledFields.join(", ")} inherited from the active Engineering Session. Edit any field as needed.
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Well name (required)">
            <TextInput value={rec.wellName} onChange={(v) => set({ wellName: v })} testId="well-wellName" />
          </Field>
          <Field label="UWI / API number" helper="Optional. Must be unique if entered.">
            <TextInput value={rec.uwi} onChange={(v) => set({ uwi: v })} testId="well-uwi" />
          </Field>
          <Field label="Operator">
            <TextInput value={rec.operator} onChange={(v) => set({ operator: v })} testId="well-operator" />
          </Field>
          <Field label="Client">
            <TextInput value={rec.client} onChange={(v) => set({ client: v })} testId="well-client" />
          </Field>
          <Field label="Field">
            <TextInput value={rec.field} onChange={(v) => set({ field: v })} testId="well-field" />
          </Field>
          <Field label="Block">
            <TextInput value={rec.block} onChange={(v) => set({ block: v })} testId="well-block" />
          </Field>
          <Field label="Country">
            <TextInput value={rec.country} onChange={(v) => set({ country: v })} testId="well-country" />
          </Field>
          <Field label="Rig name / number">
            <TextInput value={rec.rigName} onChange={(v) => set({ rigName: v })} testId="well-rigName" />
          </Field>
          <Field label="Rig contractor">
            <TextInput value={rec.rigContractor} onChange={(v) => set({ rigContractor: v })} testId="well-rigContractor" />
          </Field>
        </div>
        <SectionSaveBar sectionKey="identity" onSave={handleSave} sectionErrors={sectionErrors} />
      </section>
      </div>

      {/* 2. Classification -------------------------------------------------*/}
      <div id="well-section-classification">
      <section className="card-rs p-4">
        <SectionHeader title="Classification" sectionKey="classification" rec={rec} isNew={isNew} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Well type">
            <NativeSelect value={rec.wellType} onChange={(v) => set({ wellType: v })}
                          options={WELL_TYPES.map(t => ({ value: t, label: t }))}
                          testId="well-wellType" />
          </Field>
          <Field label="Purpose">
            <NativeSelect value={rec.wellPurpose} onChange={(v) => set({ wellPurpose: v })}
                          options={WELL_PURPOSES.map(t => ({ value: t, label: t }))}
                          testId="well-wellPurpose" />
          </Field>
          <Field label="Objective (optional)" helper="Operational classification for reporting.">
            <NativeSelect value={rec.wellObjective} onChange={(v) => set({ wellObjective: v })}
                          options={WELL_OBJECTIVES.map(t => ({ value: t, label: t || "— none —" }))}
                          testId="well-wellObjective" />
          </Field>
          <Field label="Environment (required)"
                 helper={envLocked ? "Resolved from the global Subsea operating mode." : undefined}>
            <NativeSelect value={effectiveEnvironment} onChange={(v) => set({ environment: v })}
                          options={ENVIRONMENTS.map(t => ({ value: t, label: t }))}
                          disabled={envLocked}
                          testId="well-environment" />
          </Field>
          <Field label={offshore ? "BOP location (required)" : "BOP location"}
                 helper="Resolved from the global operating mode (Surface/Subsea) — set on the Dashboard.">
            <NativeSelect value={effectiveBopLocation}
                          onChange={(v) => set({
                            bopLocation: v,
                            // A subsea BOP stack is inherently offshore — the
                            // well's own Environment field must stay truthful
                            // (Master Correction Pass, Defect 6). Never flips
                            // the reverse way: picking "surface" says nothing
                            // about onshore vs. offshore on its own.
                            ...(v === "subsea" ? { environment: "offshore" } : {}),
                          })}
                          options={BOP_LOCATIONS.map(t => ({ value: t, label: t }))}
                          disabled
                          testId="well-bopLocation" />
          </Field>
        </div>
        <SectionSaveBar sectionKey="classification" onSave={handleSave} sectionErrors={sectionErrors} />
      </section>
      </div>

      {/* 3. Depths & Datums ------------------------------------------------*/}
      <div id="well-section-depths">
      <section className="card-rs p-4">
        <SectionHeader title="Depths & Datums (ft)" sectionKey="depths" rec={rec} isNew={isNew} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {offshore && (
            <Field label="Water depth (required)">
              <NumIn value={rec.waterDepthFt} onChange={(v) => set({ waterDepthFt: v })} testId="well-waterDepthFt" />
            </Field>
          )}
          {/* D3 fix (Five-Defect Correction Pass) — Air Gap is a marine
              rig-floor-clearance field, meaningless for an onshore well.
              Gated by the same existing `offshore` flag (rec.environment)
              already used for Water depth above — no new environment
              source, purely a display change; `airGapFt` itself is only
              ever validated as "non-negative if entered" (never required),
              so hiding it can never block section completion, and any
              value entered while offshore is never cleared by this gate. */}
          {offshore && (
            <Field label="Air gap">
              <NumIn value={rec.airGapFt} onChange={(v) => set({ airGapFt: v })} testId="well-airGapFt" />
            </Field>
          )}
          <Field label="RKB elevation">
            <NumIn value={rec.rkbElevationFt} onChange={(v) => set({ rkbElevationFt: v })} testId="well-rkbElevationFt" />
          </Field>
          {!offshore && (
            <>
              <Field label="RT to ground">
                <NumIn value={rec.rtToGroundFt} onChange={(v) => set({ rtToGroundFt: v })} testId="well-rtToGroundFt" />
              </Field>
              <Field label="Ground elevation">
                <NumIn value={rec.groundElevationFt} onChange={(v) => set({ groundElevationFt: v })} testId="well-groundElevationFt" />
              </Field>
            </>
          )}
          <Field label="Total depth — planned">
            <NumIn value={rec.totalDepthPlannedFt} onChange={(v) => set({ totalDepthPlannedFt: v })} testId="well-totalDepthPlannedFt" />
          </Field>
          <Field label="Total depth — current" helper="Must not exceed final.">
            <NumIn value={rec.totalDepthCurrentFt} onChange={(v) => set({ totalDepthCurrentFt: v })} testId="well-totalDepthCurrentFt" />
          </Field>
          <Field label="Total depth — final">
            <NumIn value={rec.totalDepthFinalFt} onChange={(v) => set({ totalDepthFinalFt: v })} testId="well-totalDepthFinalFt" />
          </Field>
          <Field label="Open-hole diameter (in)"
                 helper="Drilled diameter below the shoe. Takes precedence over the BHA bit size for every open-hole annular volume. Casing nominal size is never used here.">
            <NumIn value={rec.openHoleDiameterIn} onChange={(v) => set({ openHoleDiameterIn: v })} testId="well-openHoleDiameterIn" />
          </Field>
          <div className="sm:col-span-2 text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--rs-text-3)] mt-2">
            Formation Strength Test (LOT)
          </div>
          <Field label="LOT pressure" helper="So the Kill Sheet can inherit it for MAASP — optional, but positive if entered.">
            <NumIn value={rec.lotPressurePsi} onChange={(v) => set({ lotPressurePsi: v })} testId="well-lotPressurePsi" />
          </Field>
          <Field label="MW at LOT" helper="Mud weight during the LOT — optional, but positive if entered.">
            <NumIn value={rec.mwAtLotPpg} onChange={(v) => set({ mwAtLotPpg: v })} testId="well-mwAtLotPpg" />
          </Field>
        </div>
        <SectionSaveBar sectionKey="depths" onSave={handleSave} sectionErrors={sectionErrors} />
      </section>
      </div>

      {/* 4. Surface Location -----------------------------------------------*/}
      <div id="well-section-location">
      <section className="card-rs p-4">
        <SectionHeader title="Surface Location" sectionKey="location" rec={rec} isNew={isNew} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Latitude (°)" helper="Range -90 to 90.">
            <NumIn value={rec.latitude} onChange={(v) => set({ latitude: v })} testId="well-latitude" />
          </Field>
          <Field label="Longitude (°)" helper="Range -180 to 180.">
            <NumIn value={rec.longitude} onChange={(v) => set({ longitude: v })} testId="well-longitude" />
          </Field>
          <Field label="Coordinate reference system">
            <NativeSelect value={rec.coordRefSystem} onChange={(v) => set({ coordRefSystem: v })}
                          options={COORD_REF_SYSTEMS.map(t => ({ value: t, label: t }))}
                          testId="well-coordRefSystem" />
          </Field>
        </div>
        <SectionSaveBar sectionKey="location" onSave={handleSave} sectionErrors={sectionErrors} />
      </section>
      </div>

      {/* 5. Status & Timeline ----------------------------------------------*/}
      <div id="well-section-status">
      <section className="card-rs p-4">
        <SectionHeader title="Status & Timeline" sectionKey="status" rec={rec} isNew={isNew} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Status (required)">
            <NativeSelect value={rec.status} onChange={(v) => set({ status: v })}
                          options={WELL_STATUSES.map(t => ({ value: t, label: t }))}
                          testId="well-status" />
          </Field>
          <Field label="Spud date">
            <DateIn value={rec.spudDate} onChange={(v) => set({ spudDate: v })} testId="well-spudDate" />
          </Field>
          <Field label="Release date">
            <DateIn value={rec.releaseDate} onChange={(v) => set({ releaseDate: v })} testId="well-releaseDate" />
          </Field>
        </div>
        <SectionSaveBar sectionKey="status" onSave={handleSave} sectionErrors={sectionErrors} />
      </section>
      </div>

      {/* 6. BOP Reference --------------------------------------------------*/}
      <div id="well-section-bop">
      <section className="card-rs p-4">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h2 className="text-[13px] font-semibold">BOP Reference</h2>
          <div className="flex items-center gap-2">
            <Link to="/reference-libraries/bop"
                  state={libraryReturnState(location, rec, "well-section-bop")}
                  data-testid="well-bop-manage"
                  className="inline-flex items-center gap-1 text-[10.5px] text-amber-400">
              <Library className="w-3 h-3" /> Manage BOP Library
            </Link>
            <CompletionBadge sectionKey="bop" rec={rec} isNew={isNew} />
          </div>
        </div>
        <Field label="BOP from library" helper="References the BOP record by ID — no data is duplicated into this well.">
          <NativeSelect value={rec.bopRefId} onChange={(v) => set({ bopRefId: v })}
                        options={bopOptions} testId="well-bopRefId" />
        </Field>
        {bopContextMismatch && (
          <p className="text-[11px] text-amber-400 mt-1" data-testid="well-bop-context-mismatch">
            The selected BOP record doesn't match the current operating context (Surface/Subsea) — review before proceeding.
          </p>
        )}
        <SectionSaveBar sectionKey="bop" onSave={handleSave} sectionErrors={sectionErrors} />
      </section>
      </div>

      {/* 7. Mud Program · reuses the existing /wells/:id/mud-programs pages.
           FIRST of the engineering modules: Casing, BHA and the Kill Sheet
           all read mud weight from the active mud program via RCE (the BHA
           for its buoyancy factor, the Kill Sheet for its kill
           calculations), so defining the mud up front means that weight is
           already available by the time the operator reaches them. */}
      <div id="well-section-mud">
      <section className="card-rs p-4">
        <SectionHeader title="Mud Program" sectionKey="mud" rec={rec} isNew={isNew} />
        {isNew ? (
          <div className="text-xs text-[var(--rs-text-dim,#9aa6b2)] italic">
            Save the well first — mud programs attach to a saved well.
          </div>
        ) : (
          <ChildModuleCard
            wellId={id}
            listFn={listMudProgramsForWell}
            newHref={`/wells/${id}/mud-programs/new?return=well`}
            manageHref={`/wells/${id}/mud-programs`}
            singular="Mud Program"
            plural="Mud Programs"
            addTestId="well-section-mud-add"
            manageTestId="well-section-mud-manage"
            stackMode={stackMode}
          />
        )}
        <SectionSaveBar sectionKey="mud" onSave={handleSave} sectionErrors={sectionErrors} />
      </section>
      </div>

      {/* 8. Casing Program --------------------------------------------------*/}
      <div id="well-section-casing">
      <section className="card-rs p-4">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h2 className="text-[13px] font-semibold">Casing Program</h2>
          <div className="flex items-center gap-2">
            <Link to="/reference-libraries/casing"
                  state={libraryReturnState(location, rec, "well-section-casing")}
                  data-testid="well-casing-manage"
                  className="inline-flex items-center gap-1 text-[10.5px] text-amber-400">
              <Library className="w-3 h-3" /> Manage Casing Library
            </Link>
            <CompletionBadge sectionKey="casing" rec={rec} isNew={isNew} />
          </div>
        </div>
        {(rec.children?.casingProgram?.length || 0) === 0 ? (
          <div className="text-xs text-[var(--rs-text-dim,#9aa6b2)] italic mb-3">
            No casing strings yet. Tap <span className="text-amber-400">Add string</span> below to
            reference a record from the Casing Library — the well stores the
            library ID, not a copy of the data.
          </div>
        ) : (
          <div className="flex flex-col gap-3 mb-3" data-testid="well-casing-rows">
            {rec.children.casingProgram.map((row, idx) => (
              <div key={row.id || idx}
                   data-testid={`well-casing-row-${idx}`}
                   className="rounded-md border border-[var(--rs-border,#1f2630)] p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--rs-text-dim,#9aa6b2)]">
                    String #{idx + 1}
                  </div>
                  <button type="button" onClick={() => removeCasingRow(idx)}
                          data-testid={`well-casing-remove-${idx}`}
                          aria-label={`Remove casing string ${idx + 1}`}
                          className="p-1 rounded text-zinc-400 hover:text-red-400 hover:bg-red-500/10">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Hole section">
                    <NativeSelect value={row.holeSection}
                                  onChange={(v) => patchCasingRow(idx, { holeSection: v })}
                                  options={HOLE_SECTIONS.map(t => ({ value: t, label: t }))}
                                  testId={`well-casing-${idx}-holeSection`} />
                  </Field>
                  <Field label="Library record" helper="Optional — only needed when you have not entered the dimensions below.">
                    <NativeSelect value={row.casingRefId}
                                  onChange={(v) => pickCasing(idx, v)}
                                  options={casingOptions}
                                  testId={`well-casing-${idx}-refId`} />
                  </Field>
                  <Field label="Top depth (ft)">
                    <NumIn value={row.topDepthFt}
                           onChange={(v) => patchCasingRow(idx, { topDepthFt: v })}
                           testId={`well-casing-${idx}-top`} />
                  </Field>
                  <Field label="Bottom depth (ft)">
                    <NumIn value={row.bottomDepthFt}
                           onChange={(v) => patchCasingRow(idx, { bottomDepthFt: v })}
                           testId={`well-casing-${idx}-bottom`} />
                  </Field>
                  <Field label="Casing ID (in)"
                         helper="Internal diameter used for the cased annulus. Wins over the library value.">
                    <NumIn value={row.manualInnerDiameterIn}
                           onChange={(v) => patchCasingRow(idx, { manualInnerDiameterIn: v })}
                           testId={`well-casing-${idx}-manualId`} />
                  </Field>
                  <Field label="Casing OD (in)"
                         helper="Actual outside diameter — not the nominal size label.">
                    <NumIn value={row.manualOuterDiameterIn}
                           onChange={(v) => patchCasingRow(idx, { manualOuterDiameterIn: v })}
                           testId={`well-casing-${idx}-manualOd`} />
                  </Field>
                </div>

                {_casingOverrideNote(row) && (
                  <div data-testid={`well-casing-${idx}-override`}
                       className="mt-2 text-[10.5px] text-amber-300">
                    {row.casingRefId ? "MANUAL OVERRIDE" : "MANUAL DIMENSIONS"} · {_casingOverrideNote(row)}
                  </div>
                )}

                {row.casingRefId && (() => {
                  // Audit trail: shows what the library record itself holds,
                  // so a later manual override stays comparable against the
                  // source (the amber MANUAL OVERRIDE note above flags the
                  // divergence).
                  const _libRec = findById(row.casingRefId);
                  return (
                    <div className="mt-2 text-[10.5px] font-mono text-[var(--rs-text-dim,#9aa6b2)]">
                      Library reference (display only) · nominal size{" "}
                      {row.nominalSize || "—"} · {row.grade || "—"} · {row.connection || "—"}
                      {row.weightPpf ? ` · ${row.weightPpf} ppf` : ""}
                      {Number.isFinite(Number(_libRec?.outerDiameter)) ? ` · OD ${_libRec.outerDiameter} in` : ""}
                      {Number.isFinite(Number(_libRec?.innerDiameter)) ? ` · ID ${_libRec.innerDiameter} in` : ""}
                    </div>
                  );
                })()}

                <div className="mt-2">
                  <Field label="Notes">
                    <TextInput value={row.notes}
                               onChange={(v) => patchCasingRow(idx, { notes: v })}
                               testId={`well-casing-${idx}-notes`} />
                  </Field>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-start">
          <button type="button" onClick={addCasingRow}
                  data-testid="well-casing-add"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[11.5px] font-medium border border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
                  style={{ minHeight: 40 }}>
            <Plus className="w-3.5 h-3.5" /> Add string
          </button>
        </div>
        <SectionSaveBar sectionKey="casing" onSave={handleSave} sectionErrors={sectionErrors} />
      </section>
      </div>

      {/* 9. BHA · reuses the existing /wells/:id/bha editor pages.  ---------
          Shows a summary + "Add BHA" / "Manage BHAs" buttons that open the
          existing editor; SectionSaveBar scrolls to Drill String on save. */}
      <div id="well-section-bha">
      <section className="card-rs p-4">
        <SectionHeader title="BHA" sectionKey="bha" rec={rec} isNew={isNew} />
        {isNew ? (
          <div className="text-xs text-[var(--rs-text-dim,#9aa6b2)] italic">
            Save the well first — BHAs attach to a saved well.
          </div>
        ) : (
          <ChildModuleCard
            wellId={id}
            listFn={listBhasForWell}
            newHref={`/wells/${id}/bha/new?return=well`}
            manageHref={`/wells/${id}/bha`}
            singular="BHA"
            plural="BHAs"
            addTestId="well-section-bha-add"
            manageTestId="well-section-bha-manage"
            stackMode={stackMode}
          />
        )}
        <SectionSaveBar sectionKey="bha" onSave={handleSave} sectionErrors={sectionErrors} />
      </section>
      </div>

      {/* 10. Drill String · reuses the existing /wells/:id/drill-strings pages. */}
      <div id="well-section-drillstring">
      <section className="card-rs p-4">
        <SectionHeader title="Drill String" sectionKey="drillstring" rec={rec} isNew={isNew} />
        {isNew ? (
          <div className="text-xs text-[var(--rs-text-dim,#9aa6b2)] italic">
            Save the well first — drill strings attach to a saved well.
          </div>
        ) : (
          <ChildModuleCard
            wellId={id}
            listFn={listDrillStringsForWell}
            newHref={`/wells/${id}/drill-strings/new?return=well`}
            manageHref={`/wells/${id}/drill-strings`}
            singular="Drill String"
            plural="Drill Strings"
            addTestId="well-section-drillstring-add"
            manageTestId="well-section-drillstring-manage"
            stackMode={stackMode}
          />
        )}
        <SectionSaveBar sectionKey="drillstring" onSave={handleSave} sectionErrors={sectionErrors} />
      </section>
      </div>

      {/* 11. Kill Sheet · reuses the existing /wells/:id/kill-sheets pages. */}
      <div id="well-section-killsheet">
      <section className="card-rs p-4">
        <SectionHeader title="Kill Sheet" sectionKey="killsheet" rec={rec} isNew={isNew} />
        {isNew ? (
          <div className="text-xs text-[var(--rs-text-dim,#9aa6b2)] italic">
            Save the well first — kill sheets attach to a saved well.
          </div>
        ) : (
          <ChildModuleCard
            wellId={id}
            listFn={listKillSheetsForWell}
            newHref={`/wells/${id}/kill-sheets/new?return=well`}
            manageHref={`/wells/${id}/kill-sheets`}
            singular="Kill Sheet"
            plural="Kill Sheets"
            addTestId="well-section-killsheet-add"
            manageTestId="well-section-killsheet-manage"
            stackMode={stackMode}
          />
        )}
        {/* Names the sections the Kill Sheet is still waiting on, rather than
            leaving the operator to infer it from scattered badges. */}
        {!isNew && (() => {
          const missing = missingKillSheetPrerequisites(rec);
          if (missing.length === 0) {
            return (
              <div data-testid="well-killsheet-ready"
                   className="mt-3 text-[11px] flex items-center gap-1.5"
                   style={{ color: "#78c8ff" }}>
                <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                All Kill Sheet inputs are in place.
              </div>
            );
          }
          const labelFor = (k) =>
            WELL_SECTION_ORDER.find((s) => s.key === k)?.label || k;
          return (
            <div data-testid="well-killsheet-blockers"
                 className="mt-3 rounded-md border px-3 py-2 text-[11px] leading-relaxed"
                 style={{ borderColor: "rgba(245,180,0,0.45)", background: "rgba(245,180,0,0.06)", color: "var(--rs-text-2)" }}>
              <div className="font-semibold mb-1" style={{ color: "var(--rs-amber)" }}>
                Kill Sheet inputs still needed
              </div>
              <ul className="list-disc list-inside space-y-0.5">
                {missing.map((k) => (
                  <li key={k}>
                    <button type="button"
                            data-testid={`well-killsheet-blocker-${k}`}
                            onClick={() => document.getElementById(`well-section-${k}`)
                              ?.scrollIntoView({ behavior: "smooth", block: "start" })}
                            className="underline underline-offset-2 hover:text-[var(--rs-amber)]">
                      {labelFor(k)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })()}
        <SectionSaveBar sectionKey="killsheet" onSave={handleSave} sectionErrors={sectionErrors} />
      </section>
      </div>

      {/* 10. Tubing · completion-phase component. Not applicable to most
          drilling operations; presented for continuity of the engineering
          sequence. No new storage; no schema change. */}
      <div id="well-section-tubing">
      <section className="card-rs p-4">
        <SectionHeader title="Tubing" sectionKey="tubing" rec={rec} isNew={isNew} />
        <div className="rounded-md border border-dashed border-[var(--rs-border,#1f2630)] p-3 text-[11.5px] text-[var(--rs-text-2)] leading-snug"
             data-testid="well-section-tubing-info">
          Tubing applies to completion wells. This section is <strong>optional</strong> for
          drilling operations and will be enabled for completion workflows in a future
          release. You can safely continue.
        </div>
        <SectionSaveBar sectionKey="tubing" onSave={handleSave} sectionErrors={sectionErrors} />
      </section>
      </div>

      {/* 11. Notes ---------------------------------------------------------*/}
      <div id="well-section-notes">
      <section className="card-rs p-4">
        <SectionHeader title="Notes" sectionKey="notes" rec={rec} isNew={isNew} />
        <Field label="Free-text notes">
          <TextArea value={rec.notes} onChange={(v) => set({ notes: v })} testId="well-notes" rows={4} />
        </Field>
        <SectionSaveBar sectionKey="notes" onSave={handleSave} sectionErrors={sectionErrors} lastSection />
      </section>
      </div>

      {/* Related child modules — BHA + Drill String are now surfaced as
          inline sections above. Retained here as compact links to Kill Sheets,
          Mud Programs and Cement Programs (which live on their own routes and
          are not part of the Well Setup sequence). */}
      {!isNew && (
        <section className="card-rs p-4">
          <h2 className="text-[13px] font-semibold mb-3">Related Modules</h2>
          <div className="flex flex-col gap-2">
            <Link to={`/wells/${id}/kill-sheets`}
                  data-testid="well-open-killsheets"
                  className={`inline-flex items-center justify-between gap-2 px-3 py-2 rounded-md border ${accent.border30} ${accent.hoverBg10}`}>
              <span className={`text-sm font-semibold ${accent.text}`}>Kill Sheets</span>
              <span className={`font-mono text-[11px] ${accent.text}`}>Open →</span>
            </Link>
            <Link to={`/wells/${id}/mud-programs`}
                  data-testid="well-open-mud-programs"
                  className={`inline-flex items-center justify-between gap-2 px-3 py-2 rounded-md border ${accent.border30} ${accent.hoverBg10}`}>
              <span className={`text-sm font-semibold ${accent.text}`}>Mud Programs</span>
              <span className={`font-mono text-[11px] ${accent.text}`}>Open →</span>
            </Link>
            <div className="text-[10.5px] text-[var(--rs-text-dim,#9aa6b2)] px-1">
              Cement Program · Daily Reports will appear here as later modules ship.
            </div>
          </div>
        </section>
      )}

      {/* Final SAVE ALL PROFILES — visual confirmation of setup completion. */}
      {!isNew && (
        <div id="well-section-complete" data-testid="well-save-all-profiles-wrap"
             className="card-rs p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
             style={{ background: "rgba(120,200,255,0.05)", borderColor: "rgba(120,200,255,0.4)" }}>
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.18em]" style={{ color: "#78c8ff" }}>
              Engineering Setup · Final Confirmation
            </div>
            <div className="text-[12.5px] text-[var(--rs-text-1)] mt-1">
              You have reached the end of the Well Profile. Kill Sheets and Engineering Reports will now auto-populate.
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button type="button"
                    onClick={() => handleSave()}
                    data-testid="well-save-all-profiles"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-semibold"
                    style={{ background: "var(--rs-amber)", color: "#0A0B0E" }}>
              <Save className="w-4 h-4" /> Save All Profiles
            </button>
            <button type="button"
                    onClick={() => handleSave(undefined, OPEN_SESSION_SWITCHER)}
                    data-testid="well-save-all-next"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-semibold"
                    style={{ background: "transparent", color: "#78c8ff", border: "1px solid rgba(120,200,255,0.5)" }}>
              Next → Rig Engineering
            </button>
          </div>
        </div>
      )}

      {/* Floating Save — mirrors the pattern used in BHA / DrillString / Kill
          Sheet editors so the Save action is always reachable regardless of
          scroll position on this long form. */}
      <FloatingActionToolbar
        testId="well-fab"
        actions={[
          { id: "save", icon: Save, label: "Save", onClick: handleSave,
            testId: "fab-well-save", primary: true },
        ]}
      />

      <UnsavedChangesDialog
        open={unsaved.dialogOpen}
        onSaveAndLeave={unsaved.handleSaveAndLeave}
        onDiscard={unsaved.handleDiscard}
        onCancel={unsaved.handleCancel}
      />
    </div>
  );
}


// =============================================================================
// Well Status Summary — operational dashboard placeholder.
//
// Pass 1 surfaces every value that already exists on the WellProfile and
// renders "—" for fields that will be filled in by future modules (BHA,
// drill string, mud program, etc.). The cell layout is stable so future
// passes only swap the "—" for a live value — no redesign needed.
// =============================================================================

function _summaryBopLabel(rec) {
  if (!rec.bopRefId) return "—";
  const ref = findById(rec.bopRefId);
  if (!ref) return rec.bopRefId;
  return [ref.stackName, ref.nominalSize, ref.workingPressure ? `${ref.workingPressure} psi` : null]
    .filter(Boolean).join(" · ");
}

function _summaryCurrentCasing(rec) {
  const csg = rec.children?.casingProgram || [];
  if (csg.length === 0) return "—";
  // Pick the deepest string that has a bottom depth — that's the "current"
  // string the well has reached. Falls back to the last row if depths are
  // all blank.
  const withDepth = csg.filter((r) => Number.isFinite(Number(r.bottomDepthFt)));
  const candidate = withDepth.length
    ? withDepth.reduce((a, b) => (Number(a.bottomDepthFt) >= Number(b.bottomDepthFt) ? a : b))
    : csg[csg.length - 1];
  const parts = [candidate.holeSection || null, candidate.nominalSize || null, candidate.grade || null];
  return parts.filter(Boolean).join(" · ") || "—";
}

function _summaryCurrentHoleSection(rec) {
  const csg = rec.children?.casingProgram || [];
  if (csg.length === 0) return "—";
  return csg[csg.length - 1].holeSection || "—";
}

function _resolvedDisplay(r, suffix) {
  if (!r || r.confidence === "unknown" || r.confidence === "conflict") return "—";
  if (r.value === "" || r.value == null) return "—";
  return `${r.value}${suffix || ""}`;
}

function _depth(v) {
  if (v === "" || v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toLocaleString()} ft`;
}

function _summaryCurrentBha(rec) {
  const bhas = rec.children?.bha || [];
  if (bhas.length === 0) return "—";
  // Prefer the one that is currently "in-hole"; otherwise the most recently
  // updated. Summary entries carry { id, name, bhaNumber, revision,
  // operationalStatus, bitSize, updatedAt }.
  const inHole = bhas.find((b) => b.operationalStatus === "in-hole");
  const pick = inHole || bhas[0];
  const parts = [pick.name, pick.revision && `Rev ${pick.revision}`, pick.operationalStatus];
  return parts.filter(Boolean).join(" · ");
}

function _summaryCurrentDrillString(rec) {
  const list = rec.children?.drillStrings || [];
  if (list.length === 0) return "—";
  const inHole = list.find((d) => d.operationalStatus === "in-hole");
  const pick = inHole || list[0];
  const parts = [pick.name, pick.revision && `Rev ${pick.revision}`, pick.operationalStatus];
  return parts.filter(Boolean).join(" · ");
}

// Total String Tally — DS + BHA aggregate. Pure: reads from full records.
// Returns "" cells when the contributing record is missing or geometry is
// incomplete, so the dashboard never displays a misleading partial total.
function _buildStringTally(rec) {
  if (!rec?.id) return { totalLengthFt: "", totalInternalVolumeBbl: "" };
  // Use buildWellState for the auto-picked active BHA + DS + bit depth.
  const state = buildWellState({ wellId: rec.id });
  const stringIntVol = state.snapshot.stringInternalVolBbl;

  // Aggregate total length: DS summary totalLengthFt + BHA components sum.
  const dsList = rec.children?.drillStrings || [];
  const inHole = dsList.find((d) => d.operationalStatus === "in-hole");
  const dsPick = inHole || dsList[0];
  const dsLen = (dsPick && dsPick.totalLengthFt !== "" && dsPick.totalLengthFt != null)
    ? Number(dsPick.totalLengthFt) : null;

  let bhaLen = null;
  if (state.sources.bhaId) {
    const bha = getBha(state.sources.bhaId);
    if (bha) {
      const baked = computeBhaGeometry(bha);
      // BHA total length = top of the last component.
      const last = baked.components[baked.components.length - 1];
      if (last && Number.isFinite(Number(last.cumulativeLengthFt))) {
        bhaLen = Number(last.cumulativeLengthFt);
      }
    }
  }
  const totalLen = (dsLen != null && bhaLen != null) ? dsLen + bhaLen
                 : (dsLen != null) ? dsLen
                 : (bhaLen != null) ? bhaLen : "";
  return {
    totalLengthFt: totalLen,
    totalInternalVolumeBbl: stringIntVol,
    bitDepthMdFt: state.snapshot.currentMdFt,
  };
}

function _fmtFt(v) {
  if (v === "" || v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${Math.round(n).toLocaleString()} ft`;
}
function _fmtBbl(v, digits = 2) {
  if (v === "" || v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toLocaleString(undefined, { maximumFractionDigits: digits })} bbl`;
}

function SummaryCell({ label, value, testId }) {
  return (
    <div data-testid={testId}
         className="rounded-md border border-[var(--rs-border,#1f2630)] bg-[rgba(255,255,255,0.015)] p-2.5">
      <div className="text-[9px] uppercase tracking-[0.16em] text-[var(--rs-text-3,#6b7785)]">
        {label}
      </div>
      {/* Wraps rather than truncates: `truncate` clipped engineering values
          (depths, pressures) at mobile width, and the title tooltip that
          compensated for it is unreachable on touch. */}
      <div className="mt-1 text-[12px] font-medium text-[var(--rs-text,#e8edf2)] break-words"
           title={typeof value === "string" ? value : undefined}>
        {value || "—"}
      </div>
    </div>
  );
}

function WellStatusSummary({ rec }) {
  const tally = _buildStringTally(rec);
  // Master Correction Pass, Defect 3 — these two cells were hardcoded to "—"
  // and never wired to any source, even though the same values already
  // resolve correctly elsewhere (Kill Sheet's own Hole Size / Mud Weight
  // fields). Reuses the SAME existing RCE resolver + FIELD_REGISTRY paths —
  // no second Hole Size / Mud Weight field, no new calculation.
  const holeSizeR  = rec.id ? resolve("well.holeSizeIn", { wellId: rec.id }) : null;
  const mudWeightR = rec.id ? resolve("mud.weightInPpg", { wellId: rec.id }) : null;
  const holeSizeDisplay  = _resolvedDisplay(holeSizeR, " in");
  const mudWeightDisplay = _resolvedDisplay(mudWeightR, " ppg");
  return (
    <section data-testid="well-summary"
             className="card-rs p-3 sm:p-4">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <h2 className="text-[12px] font-semibold tracking-wide uppercase text-[var(--rs-text-2,#c2cad4)]">
          Well Status Summary
        </h2>
        <span className="text-[9.5px] uppercase tracking-[0.18em] text-[var(--rs-text-3,#6b7785)]">
          Operational dashboard · auto-fills as modules complete
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <SummaryCell label="Well name"        testId="summary-wellName"     value={rec.wellName} />
        <SummaryCell label="Operator"         testId="summary-operator"     value={rec.operator} />
        <SummaryCell label="Rig"              testId="summary-rig"          value={[rec.rigName, rec.rigContractor].filter(Boolean).join(" · ")} />
        <SummaryCell label="Environment"      testId="summary-environment"  value={rec.environment === "offshore" ? `Offshore · ${rec.bopLocation || "—"}` : "Onshore"} />
        <SummaryCell label="Current status"   testId="summary-status"       value={(rec.status || "").toUpperCase()} />
        <SummaryCell label="Current hole section" testId="summary-holeSection" value={_summaryCurrentHoleSection(rec)} />
        <SummaryCell label="Current hole size" testId="summary-holeSize"    value={holeSizeDisplay} />
        <SummaryCell label="Current depth"    testId="summary-currentDepth" value={_depth(rec.totalDepthCurrentFt)} />
        <SummaryCell label="Current casing"   testId="summary-currentCasing" value={_summaryCurrentCasing(rec)} />
        <SummaryCell label="Current BHA"      testId="summary-currentBha"   value={_summaryCurrentBha(rec)} />
        <SummaryCell label="Current mud weight" testId="summary-mudWeight"  value={mudWeightDisplay} />
        <SummaryCell label="Selected BOP"     testId="summary-bop"          value={_summaryBopLabel(rec)} />
      </div>

      {/* Total String Tally — at-a-glance sanity check from DS + BHA + Well.
          Each cell falls back to "—" when contributors are missing so the
          summary never publishes a misleading partial total. */}
      <div className="mt-3 pt-3 border-t border-[var(--rs-border,#1f2630)]"
           data-testid="well-string-tally">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--rs-text-3,#6b7785)] mb-2">
          Total String Tally
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          <SummaryCell label="Current BHA"            testId="tally-currentBha"      value={_summaryCurrentBha(rec)} />
          <SummaryCell label="Current Drill String"    testId="tally-currentDs"       value={_summaryCurrentDrillString(rec)} />
          <SummaryCell label="Total String Length"     testId="tally-totalLength"     value={_fmtFt(tally.totalLengthFt)} />
          <SummaryCell label="Bit Depth"               testId="tally-bitDepth"        value={_fmtFt(tally.bitDepthMdFt)} />
          <SummaryCell label="Total Internal Capacity" testId="tally-totalIntVol"     value={_fmtBbl(tally.totalInternalVolumeBbl, 2)} />
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// Continue-Setup panel
// -----------------------------------------------------------------------------
// Purpose (Commercial Workflow QA — Fix Pack 3 · Issue 2):
//   After a Well is saved, users must SEE the next step without hunting.
//   This panel appears near the top of the editor (right after WellStatus
//   Summary) so it is always in the initial viewport post-save.
//
// Contents:
//   1) Optional "auto-linked to Rig" success chip (Issue 1 · single-rig case).
//   2) Optional rig-picker chip when 2+ rig sheets exist and none is active.
//   3) A "Continue setup" strip: Rig ✓ · Well ✓ · Casing ✓ · BHA (next) · …
//
// This panel is presentation-only. It reads existing storage (rig sheets,
// bha, drill strings, kill sheets) and does not mutate any engineering data
// beyond the single `linkedWellId` write triggered from the parent editor
// when the operator picks a rig.
// =============================================================================
function ContinueSetupPanel({
  wellId,
  autoLinkedRig,
  onDismissAutoLink,
  rigChoicePending,
  onPickRig,
  onDismissRigChoice,
}) {
  // These are cheap localStorage reads (small arrays). Recompute every render
  // rather than memoising, so post-auto-link state (Rig ✓) reflects immediately.
  const rigs     = listRigSheets();
  const bhaCount = wellId ? listBhasForWell(wellId).length : 0;
  const dsCount  = wellId ? listDrillStringsForWell(wellId).length : 0;
  const ksCount  = wellId ? listKillSheetsForWell(wellId).length : 0;

  // Determine next step in the engineering setup chain.
  const steps = [
    { key: "rig",   label: "Rig",         done: rigs.some((r) => (r.payload?.linkedWellId || r.linkedWellId) === wellId), href: null },
    { key: "well",  label: "Well",        done: true, href: null },
    { key: "bha",   label: "BHA",         done: bhaCount > 0, href: `/wells/${wellId}/bha/new` },
    { key: "ds",    label: "Drill String", done: dsCount > 0, href: `/wells/${wellId}/drill-strings/new` },
    { key: "ks",    label: "Kill Sheet",  done: ksCount > 0, href: `/wells/${wellId}/kill-sheets/new` },
  ];
  const nextIdx = steps.findIndex((s) => !s.done);

  return (
    <section data-testid="continue-setup-panel"
             className="card-rs p-3 sm:p-4"
             style={{
               borderColor: "rgba(245,180,0,0.35)",
               background: "rgba(245,180,0,0.03)",
             }}>
      {/* Auto-linked confirmation chip */}
      {autoLinkedRig && (
        <div data-testid="continue-setup-autolink"
             className="flex items-start justify-between gap-3 mb-3 rounded-md px-3 py-2"
             style={{ background: "rgba(120,200,255,0.06)", border: "1px solid rgba(120,200,255,0.35)" }}>
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.16em]" style={{ color: "#78c8ff" }}>
              Auto-linked
            </div>
            <div className="text-[12.5px] text-[var(--rs-text-1)] mt-0.5">
              This well is now linked to <b>{autoLinkedRig.rigName}</b> for automatic engineering inheritance.
            </div>
          </div>
          <button type="button" onClick={onDismissAutoLink}
                  data-testid="continue-setup-autolink-dismiss"
                  className="text-[11px] text-[var(--rs-text-3)] hover:text-[var(--rs-text-1)] shrink-0">
            Dismiss
          </button>
        </div>
      )}

      {/* Rig-choice picker — appears only when >=2 rig sheets exist and none
          was auto-linked. Never a modal, never blocking. */}
      {rigChoicePending && rigs.length >= 2 && (
        <div data-testid="continue-setup-rigchoice"
             className="mb-3 rounded-md px-3 py-2"
             style={{ background: "rgba(245,180,0,0.06)", border: "1px solid rgba(245,180,0,0.45)" }}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase tracking-[0.16em] text-[var(--rs-amber)]">
                Link this Well to a Rig Data Sheet
              </div>
              <div className="text-[12px] text-[var(--rs-text-2)] mt-0.5">
                Multiple Rig Data Sheets exist. Pick the rig currently drilling this well.
              </div>
            </div>
            <button type="button" onClick={onDismissRigChoice}
                    data-testid="continue-setup-rigchoice-dismiss"
                    className="text-[11px] text-[var(--rs-text-3)] hover:text-[var(--rs-text-1)] shrink-0">
              Not now
            </button>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {rigs.map((r) => {
              const rLinked = r.payload?.linkedWellId || r.linkedWellId || "";
              const rName   = r.payload?.rigName || r.label || "(unnamed rig)";
              const dis     = Boolean(rLinked && rLinked !== wellId);
              return (
                <button key={r.id}
                        type="button"
                        onClick={() => onPickRig(r.id)}
                        data-testid={`continue-setup-rig-${r.id}`}
                        disabled={dis}
                        title={dis ? "Already linked to another well" : ""}
                        className="px-2.5 py-1 rounded-md text-[11.5px] font-medium border transition-colors"
                        style={{
                          borderColor: "rgba(245,180,0,0.5)",
                          color: dis ? "var(--rs-text-3)" : "var(--rs-amber)",
                          background: "rgba(245,180,0,0.06)",
                          opacity: dis ? 0.55 : 1,
                        }}>
                  {rName}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Continue-setup strip */}
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="text-[10.5px] uppercase tracking-[0.16em] text-[var(--rs-amber)]">
          Continue Engineering Setup
        </div>
        {nextIdx >= 0 && steps[nextIdx].href && (
          <Link to={steps[nextIdx].href}
                data-testid={`continue-setup-next-${steps[nextIdx].key}`}
                className="inline-flex items-center gap-1 text-[11.5px] font-medium px-2.5 py-1 rounded-md"
                style={{ background: "var(--rs-amber)", color: "#0A0B0E" }}>
            Continue → {steps[nextIdx].label}
          </Link>
        )}
      </div>
      <ol data-testid="continue-setup-steps"
          className="flex items-center flex-wrap gap-1.5 text-[11.5px]">
        {steps.map((s, i) => {
          const isNext = i === nextIdx;
          const isDone = s.done;
          const label = isDone ? `${s.label} ✓`
                       : isNext ? `${s.label} · next`
                       : s.label;
          const style = isDone
            ? { color: "#78c8ff", background: "rgba(120,200,255,0.08)", borderColor: "rgba(120,200,255,0.35)" }
            : isNext
              ? { color: "#0A0B0E", background: "var(--rs-amber)", borderColor: "var(--rs-amber)" }
              : { color: "var(--rs-text-3)", background: "transparent", borderColor: "var(--rs-border,#1f2630)" };
          const inner = (
            <span data-testid={`continue-setup-chip-${s.key}`}
                  className="inline-flex items-center px-2.5 py-1 rounded-full border font-medium"
                  style={style}>
              {label}
            </span>
          );
          return (
            <li key={s.key} className="inline-flex items-center gap-1.5">
              {s.href && !isDone ? (
                <Link to={s.href} data-testid={`continue-setup-link-${s.key}`}>{inner}</Link>
              ) : inner}
              {i < steps.length - 1 && (
                <span className="text-[var(--rs-text-3)] px-0.5" aria-hidden>→</span>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}



// =============================================================================
// Well-section per-section polish — Commercial Workflow QA (Well Profile Polish).
// Presentation-only helpers: they compute per-section completion from `rec` and
// render a local Save button beneath each section. No schema/storage change.
// The whole record is still persisted through `updateWellProfile` / `create
// WellProfile`; per-section save is a UX convenience that scrolls the operator
// to the next section after a successful save.
// =============================================================================
// Single source of truth, shared with the child editors so a reorder can
// never again leave them navigating to the wrong "next" section.
const WELL_SECTION_ORDER = WELL_SECTION_FLOW;

// WELL_REQUIRED_SECTIONS / _sectionRequired / _sectionHasData now live in
// lib/well/completion.js (imported at the top of this file) — the Dashboard
// needs the identical criteria, and a second copy here would drift.

// Save vs. Complete correction (Support correction — Aug 2026). Pressing
// Save must never, by itself, mean a section is Complete. This now returns
// a tri-state result:
//   "complete"   — a required section whose completion criteria are met
//   "incomplete" — a required section that's been saved but doesn't meet
//                  its criteria yet (was previously mislabelled Completed
//                  for Tubing/Notes; other required sections already fell
//                  through to no badge, which is preserved as "incomplete")
//   "optional"   — a genuinely optional section with no data yet
// Returns null before the well's first save (no badge on a draft).
function _sectionStatus(rec, key, isNew) {
  if (isNew) return null;
  const hasData = _sectionHasData(rec, key);
  if (hasData) return "complete";
  if (_sectionRequired(rec, key)) return "incomplete";
  // Not required for the Well Profile record, but the Kill Sheet cannot be
  // produced without it — "Optional" was actively misleading here.
  if (isKillSheetPrerequisite(key)) return "needed-for-killsheet";
  // The Kill Sheet section is the OUTPUT of this workflow, not an input to
  // it, so it is never labelled optional either — see the Kill Sheet
  // section's own readiness strip.
  if (key === "killsheet") return null;
  return "optional";
}

// Back-compat boolean for _nextSectionKey's "skip past this section" check.
// Optional sections never block auto-advance, matching the prior behaviour
// for Tubing/Notes (previously always true) and extending it consistently
// to the rest of the optional set.
function _sectionComplete(rec, key, isNew) {
  if (isNew) return false;
  return _sectionRequired(rec, key) ? _sectionHasData(rec, key) : true;
}

function _nextSectionKey(rec, currentKey, isNew) {
  const idx = WELL_SECTION_ORDER.findIndex((s) => s.key === currentKey);
  if (idx === -1) return null;
  for (let i = idx + 1; i < WELL_SECTION_ORDER.length; i++) {
    const k = WELL_SECTION_ORDER[i].key;
    if (!_sectionComplete(rec, k, isNew)) return k;
  }
  return null; // all sections after this one are complete
}

function CompletionBadge({ sectionKey, rec, isNew }) {
  const status = _sectionStatus(rec, sectionKey, isNew);
  if (!status) return null;
  if (status === "complete") {
    return (
      <span data-testid={`well-section-${sectionKey}-done`}
            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded"
            style={{ background: "rgba(120,200,255,0.14)", color: "#78c8ff", border: "1px solid rgba(120,200,255,0.4)" }}>
        <CheckCircle2 className="w-3 h-3" /> Completed
      </span>
    );
  }
  if (status === "incomplete") {
    return (
      <span data-testid={`well-section-${sectionKey}-incomplete`}
            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded"
            style={{ background: "rgba(255,150,60,0.14)", color: "#ff9c3c", border: "1px solid rgba(255,150,60,0.4)" }}>
        <AlertCircle className="w-3 h-3" /> Saved · Incomplete
      </span>
    );
  }
  if (status === "needed-for-killsheet") {
    return (
      <span data-testid={`well-section-${sectionKey}-needed`}
            title="Not required to complete the Well Profile, but the Kill Sheet cannot be calculated without it."
            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded"
            style={{ background: "rgba(245,180,0,0.12)", color: "var(--rs-amber)", border: "1px solid rgba(245,180,0,0.45)" }}>
        <AlertCircle className="w-3 h-3" /> Required for Kill Sheet
      </span>
    );
  }
  // status === "optional"
  return (
    <span data-testid={`well-section-${sectionKey}-optional`}
          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded"
          style={{ background: "rgba(154,166,178,0.14)", color: "#9aa6b2", border: "1px solid rgba(154,166,178,0.35)" }}>
      Optional
    </span>
  );
}

function SectionHeader({ title, sectionKey, rec, isNew, inline = false }) {
  const badge = <CompletionBadge sectionKey={sectionKey} rec={rec} isNew={isNew} />;
  if (inline) {
    return (
      <div className="inline-flex items-center gap-2">
        <h2 className="text-[13px] font-semibold">{title}</h2>
        {badge}
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
      <h2 className="text-[13px] font-semibold">{title}</h2>
      {badge}
    </div>
  );
}

function SectionSaveBar({ sectionKey, onSave, lastSection = false, sectionErrors = {} }) {
  const label = lastSection ? "Save Well Profile" : `Save ${WELL_SECTION_ORDER.find(s => s.key === sectionKey)?.label || ""}`;
  const nextKey = React.useMemo(() => {
    const idx = WELL_SECTION_ORDER.findIndex((s) => s.key === sectionKey);
    if (idx === -1 || idx === WELL_SECTION_ORDER.length - 1) return null;
    return WELL_SECTION_ORDER[idx + 1].key;
  }, [sectionKey]);
  // Local, section-scoped errors (follow-up, ticket 237121) — shown right
  // next to the button that produced them, so a failed Save surfaces its
  // reason here instead of pulling the page up to a top-of-form banner.
  const myErrors = sectionErrors[sectionKey] || [];
  return (
    <div className="mt-4 pt-3 border-t" style={{ borderColor: "rgba(245,180,0,0.15)" }}>
      {myErrors.length > 0 && (
        <div data-testid={`well-section-${sectionKey}-error`}
             className="mb-2 flex items-start gap-1.5 text-[11px] text-red-300">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <ul className="list-disc list-inside space-y-0.5">
            {myErrors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}
      <div className="flex items-center justify-end">
        <button type="button"
                onClick={() => onSave(nextKey, undefined, sectionKey)}
                data-testid={`well-section-${sectionKey}-save`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold"
                style={{ background: "var(--rs-amber)", color: "#0A0B0E" }}>
          <Save className="w-3.5 h-3.5" /> {label}
        </button>
      </div>
    </div>
  );
}

// ChildModuleCard — presentation-only wrapper for BHA / Drill String inline
// sections in the Well Profile. Reuses the existing editors at their existing
// routes (no changes to those pages). Shows count + "Add" and "Manage" links.
function ChildModuleCard({
  wellId, listFn, newHref, manageHref, singular, plural, addTestId, manageTestId, stackMode,
}) {
  const records = listFn(wellId || "");
  const count = records.length;
  const currentName = count > 0 ? (records[records.length - 1]?.name || records[records.length - 1]?.label || "") : "";
  const accent = stackAccentClasses(stackMode);
  return (
    <div className="rounded-md border border-[var(--rs-border,#1f2630)] p-3 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10.5px] uppercase tracking-[0.16em] text-[var(--rs-text-dim,#9aa6b2)]">
            {count === 0 ? `No ${plural.toLowerCase()} yet` : `${count} ${count === 1 ? singular.toLowerCase() : plural.toLowerCase()} on this well`}
          </div>
          {currentName && (
            <div className="text-[12px] text-[var(--rs-text-1)] mt-0.5 truncate">
              Latest · <span className="font-medium">{currentName}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link to={newHref}
                data-testid={addTestId}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11.5px] font-medium border ${accent.border40} ${accent.text} ${accent.hoverBg10}`}>
            <Plus className="w-3 h-3" /> Add {singular}
          </Link>
          {count > 0 && (
            <Link to={manageHref}
                  data-testid={manageTestId}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11.5px] font-medium border border-sky-500/40 text-sky-300 hover:bg-sky-500/10">
              Manage {plural} →
            </Link>
          )}
        </div>
      </div>
      {count === 0 && (
        <div className="text-[11px] text-[var(--rs-text-2)] leading-snug">
          Tap <span className={accent.text}>Add {singular}</span> to open the {singular} editor.
          Come back to this page when done — the section will show <strong>Completed</strong>.
        </div>
      )}
    </div>
  );
}



function WellSectionsProgress({ rec, isNew }) {
  const items = WELL_SECTION_ORDER.map((s) => ({
    ...s,
    done: _sectionComplete(rec, s.key, isNew),
  }));
  const nextIdx = items.findIndex((s) => !s.done);
  return (
    <section data-testid="well-sections-progress"
             className="card-rs p-3 sm:p-4"
             style={{
               borderColor: "rgba(120,200,255,0.35)",
               background: "rgba(120,200,255,0.03)",
             }}>
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="text-[10.5px] uppercase tracking-[0.16em]" style={{ color: "#78c8ff" }}>
          Engineering Setup · Well Sections
        </div>
        <span className="text-[10px] text-[var(--rs-text-3)]">
          {items.filter((s) => s.done).length} of {items.length} complete
        </span>
      </div>
      <ol className="flex items-center flex-wrap gap-1.5 text-[11.5px]">
        {items.map((s, i) => {
          const isNext = i === nextIdx;
          const style = s.done
            ? { color: "#78c8ff", background: "rgba(120,200,255,0.10)", borderColor: "rgba(120,200,255,0.45)" }
            : isNext
              ? { color: "#0A0B0E", background: "var(--rs-amber)", borderColor: "var(--rs-amber)" }
              : { color: "var(--rs-text-3)", background: "transparent", borderColor: "var(--rs-border,#1f2630)" };
          const label = s.done ? `${s.label} ✓` : isNext ? `${s.label} · next` : s.label;
          return (
            <li key={s.key} className="inline-flex items-center gap-1.5">
              <a href={`#well-section-${s.key}`}
                 data-testid={`well-progress-chip-${s.key}`}
                 onClick={(e) => {
                   e.preventDefault();
                   const el = document.getElementById(`well-section-${s.key}`);
                   if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                 }}
                 className="inline-flex items-center px-2.5 py-1 rounded-full border font-medium"
                 style={style}>
                {label}
              </a>
              {i < items.length - 1 && (
                <span className="text-[var(--rs-text-3)] px-0.5" aria-hidden>→</span>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
